import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatUSD } from "../lib/api";
import { CheckCircle2, Loader2, Package } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    document.title = "Order confirmed — Scott Burton Photography";
    let timer;
    const poll = async () => {
      if (!sessionId) { setStatus("error"); return; }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid"); setOrder(data.order); return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("failed"); return; }
      } catch { /* keep trying */ }
      tries.current += 1;
      if (tries.current > 15) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center" data-testid="payment-success">
        {status === "checking" && (
          <>
            <Loader2 className="animate-spin mx-auto text-amber-glow" size={40} />
            <h1 className="font-serif text-4xl font-light mt-6">Confirming your payment…</h1>
            <p className="text-neutral-400 mt-2">One moment while we finalise your order.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto text-emerald-400" size={48} />
            <div className="overline-label mt-6 mb-2">Order confirmed</div>
            <h1 className="font-serif text-5xl font-light">Thank you.</h1>
            {order && (
              <div className="mt-8 border border-white/10 rounded-lg p-6 text-left bg-obsidian-800">
                <div className="flex justify-between"><span className="text-neutral-400">Order</span><span className="font-mono">{order.order_number}</span></div>
                <div className="flex justify-between mt-2"><span className="text-neutral-400">Item</span><span>{order.source_title}</span></div>
                <div className="flex justify-between mt-2"><span className="text-neutral-400">Total</span><span className="font-mono text-amber-glow">{formatUSD(order.total)}</span></div>
                {order.millers_order_id && (
                  <div className="flex justify-between mt-2 items-center"><span className="text-neutral-400 flex items-center gap-1"><Package size={13} /> Fulfillment</span>
                    <span className="text-sm">Sent to Miller's · {order.millers_order_id}</span></div>
                )}
                <p className="text-xs text-neutral-500 mt-4">A confirmation email is on its way. Estimated delivery 7–10 business days.</p>
              </div>
            )}
            <Link to="/account" className="inline-block mt-8 btn-amber rounded-full px-6 py-3">View my orders</Link>
          </>
        )}
        {["failed", "timeout", "error"].includes(status) && (
          <>
            <h1 className="font-serif text-4xl font-light">We couldn't confirm that.</h1>
            <p className="text-neutral-400 mt-2">If you were charged, your order will still appear under My Orders shortly.</p>
            <Link to="/account" className="inline-block mt-8 border border-white/20 rounded-full px-6 py-3 hover:border-amber-glow">Check my orders</Link>
          </>
        )}
      </div>
    </div>
  );
}
